<script setup>
/**
 * The twelve capabilities the old site stacked as separate 400px cards,
 * grouped into four themes.
 *
 * Desktop shows one theme at a time behind a row of tabs, with a shared
 * indicator that slides on transform. Positions are measured in physical
 * pixels (offsetLeft), which keeps it correct in RTL and LTR alike.
 *
 * Phones do not get tabs at all. A row of four tabs could only ever show one
 * and a half of them at 390px, so nothing conveyed that there were four
 * topics. Instead every theme is listed one after the other and the buttons
 * become jump links into the list.
 *
 * On desktop the tabs play through on their own: the active tab fills like a
 * progress bar and hands over to the next when it is full. The fill is a CSS
 * animation, so hovering the section or scrolling it away pauses it for free.
 * The first click or key press hands control to the visitor for good.
 */
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from "vue";
import SectionHeading from "../ui/SectionHeading.vue";
import FeaturePanel from "./FeaturePanel.vue";
import IconChevron from "../icons/IconChevron.vue";
import { resolveIcon } from "../icons";
import { useLanding } from "../../composables/useLanding";
import { track } from "../../composables/useAnalytics";
import { useMediaQuery } from "../../composables/useMediaQuery";
import { scrollToSection } from "../../composables/useScrollTo";
import { usePauseOffscreen } from "../../composables/usePauseOffscreen";
import { prefersReducedMotion } from "../../composables/useScrollReveal";

const content = useLanding().features;
const featureThemes = content.themes;

const isDesktop = useMediaQuery("(min-width: 900px)");

const activeIndex = ref(0);
const active = computed(() => featureThemes[activeIndex.value]);

const tablist = ref(null);
const tabRefs = ref([]);
const indicator = ref({ x: 0, w: 0 });

function measure() {
  const el = tabRefs.value[activeIndex.value];
  if (!el) return;
  indicator.value = { x: el.offsetLeft, w: el.offsetWidth };
}

const section = ref(null);
const { paused } = usePauseOffscreen(section);
const hovering = ref(false);
const autoplay = ref(false);

/** Desktop: swap the visible panel. */
async function select(index, { focus = false, auto = false } = {}) {
  if (!auto) autoplay.value = false;
  if (index === activeIndex.value) return;
  activeIndex.value = index;
  if (!auto) track("feature_tab_change", { tab: featureThemes[index].id });
  await nextTick();
  measure();
  if (focus) tabRefs.value[index]?.focus();
}

/** Phones: scroll down to that theme in the list. */
function jumpTo(theme) {
  track("feature_tab_change", { tab: theme.id });
  scrollToSection(`feature-${theme.id}`);
}

function onKeydown(event) {
  const last = featureThemes.length - 1;
  // In RTL the visual "next" tab is the one ArrowLeft points at.
  const rtl = document.documentElement.dir === "rtl";
  const forward = rtl ? "ArrowLeft" : "ArrowRight";
  const back = rtl ? "ArrowRight" : "ArrowLeft";

  let next = null;
  if (event.key === forward) next = activeIndex.value >= last ? 0 : activeIndex.value + 1;
  else if (event.key === back) next = activeIndex.value <= 0 ? last : activeIndex.value - 1;
  else if (event.key === "Home") next = 0;
  else if (event.key === "End") next = last;

  if (next !== null) {
    event.preventDefault();
    select(next, { focus: true });
  }
}

function advance() {
  select((activeIndex.value + 1) % featureThemes.length, { auto: true });
}

let resizeObserver;

onMounted(async () => {
  autoplay.value = !prefersReducedMotion();
  await nextTick();
  measure();
  if ("ResizeObserver" in window && tablist.value) {
    resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(tablist.value);
  }
});

onBeforeUnmount(() => resizeObserver?.disconnect());
</script>

<template>
  <section
    ref="section"
    class="section section--canvas"
    id="features"
    @mouseenter="hovering = true"
    @mouseleave="hovering = false"
  >
    <div class="container">
      <SectionHeading
        :eyebrow="content.eyebrow"
        :title="content.title"
      />

      <!-- Desktop: a tablist that swaps the panel below. -->
      <div
        v-if="isDesktop"
        ref="tablist"
        class="tabs"
        :class="{ 'is-holding': paused || hovering }"
        role="tablist"
        aria-label="נושאי המערכת"
        @keydown="onKeydown"
      >
        <span
          class="tabs__indicator"
          aria-hidden="true"
          :style="{
            transform: `translateX(${indicator.x}px)`,
            width: `${indicator.w}px`,
          }"
        />
        <button
          v-for="(theme, i) in featureThemes"
          :key="theme.id"
          :ref="(el) => (tabRefs[i] = el)"
          class="tabs__tab"
          :class="{ 'is-active': i === activeIndex }"
          role="tab"
          :id="`tab-${theme.id}`"
          :aria-selected="i === activeIndex ? 'true' : 'false'"
          :aria-controls="`panel-${theme.id}`"
          :tabindex="i === activeIndex ? 0 : -1"
          @click="select(i)"
        >
          <component :is="resolveIcon(theme.icon)" aria-hidden="true" />
          <span>{{ theme.label }}</span>
          <span
            v-if="autoplay && i === activeIndex"
            :key="activeIndex"
            class="tabs__progress"
            aria-hidden="true"
            @animationend="advance"
          />
        </button>
      </div>

      <!-- Phones: an index into the list below. Not tabs — every panel is
           on the page, so tab semantics would be a lie. -->
      <nav v-else class="jumps" aria-label="נושאי המערכת">
        <button
          v-for="theme in featureThemes"
          :key="theme.id"
          class="jumps__link"
          @click="jumpTo(theme)"
        >
          <component
            :is="resolveIcon(theme.icon)"
            class="jumps__icon"
            aria-hidden="true"
          />
          <span class="jumps__label">{{ theme.label }}</span>
          <IconChevron class="jumps__chevron" aria-hidden="true" />
        </button>
      </nav>

      <!-- Desktop: one panel, swapped by the tabs. -->
      <div
        v-if="isDesktop"
        class="panel"
        role="tabpanel"
        :id="`panel-${active.id}`"
        :aria-labelledby="`tab-${active.id}`"
        tabindex="0"
      >
        <Transition name="swap" mode="out-in">
          <FeaturePanel :key="active.id" :theme="active" />
        </Transition>
      </div>

      <!-- Phones: every theme, one after the other. -->
      <div v-else class="stack">
        <article
          v-for="theme in featureThemes"
          :key="theme.id"
          class="stack__item"
          :id="`feature-${theme.id}`"
        >
          <FeaturePanel :theme="theme" />
        </article>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* ------------------------------------------------------- tabs (desktop) */

/*
 * The tabs sit in a recessed track so the individual buttons read as
 * separate, pressable things rather than as a row of labels. Inactive tabs
 * keep a visible outline; the active one drops its outline and lets the dark
 * indicator show through from behind.
 */
.tabs {
  position: relative;
  display: flex;
  gap: var(--space-2);
  padding: var(--space-2);
  margin-block-end: var(--space-7);
  background: var(--canvas-2);
  border-radius: var(--radius-pill);
}

.tabs__indicator {
  position: absolute;
  left: 0;
  top: var(--space-2);
  bottom: var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--ink);
  transition: transform var(--dur-base) var(--ease-out),
    width var(--dur-base) var(--ease-out);
  will-change: transform;
}

.tabs__tab {
  position: relative;
  z-index: 1;
  flex: 1 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-5);
  border-radius: var(--radius-pill);
  border: 1px solid var(--line-strong);
  background: var(--surface);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--ink);
  white-space: nowrap;
  cursor: pointer;
  transition: color var(--dur-base) var(--ease-in-out),
    border-color var(--dur-base) var(--ease-in-out),
    background-color var(--dur-base) var(--ease-in-out),
    transform var(--dur-fast) var(--ease-out);
}

.tabs__tab:hover:not(.is-active) {
  border-color: var(--brand);
  color: var(--brand-ink);
  transform: translateY(-1px);
}

/* Transparent, so the sliding indicator behind it becomes the fill. */
.tabs__tab.is-active {
  color: var(--on-ink);
  background: transparent;
  border-color: transparent;
}

/* Autoplay: the active tab fills from the reading edge, then hands over. */
.tabs__progress {
  position: absolute;
  inset-inline: var(--space-5);
  inset-block-end: 7px;
  block-size: 2px;
  border-radius: var(--radius-pill);
  background: var(--accent);
  transform-origin: right;
  animation: tab-progress 7s linear both;
}

.tabs.is-holding .tabs__progress {
  animation-play-state: paused;
}

@keyframes tab-progress {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

.tabs__tab :deep(svg) {
  inline-size: 18px;
  block-size: 18px;
  flex-shrink: 0;
}

/* -------------------------------------------------- jump index (phones) */

.jumps {
  display: grid;
  gap: var(--space-2);
  margin-block-end: var(--space-8);
}

.jumps__link {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  inline-size: 100%;
  padding: var(--space-4);
  border: 1px solid var(--line-strong);
  border-radius: var(--radius-lg);
  background: var(--surface);
  text-align: start;
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-in-out),
    background-color var(--dur-fast) var(--ease-in-out);
}

.jumps__link:active {
  background: var(--brand-soft);
  border-color: var(--brand);
}

.jumps__icon {
  inline-size: 22px;
  block-size: 22px;
  flex-shrink: 0;
  color: var(--brand);
}

.jumps__label {
  flex: 1;
  font-size: var(--text-base);
  font-weight: var(--weight-semibold);
  color: var(--ink);
}

/* Points down the page, towards the item it scrolls to. */
.jumps__chevron {
  inline-size: 20px;
  block-size: 20px;
  flex-shrink: 0;
  color: var(--ink-subtle);
}

/* ------------------------------------------------- stacked list (phones) */

.stack {
  display: grid;
  gap: var(--space-9);
}

/*
 * No scroll-margin here: html already sets scroll-padding-top to clear the
 * sticky nav, and setting both makes scrollIntoView add them together.
 */

/* ----------------------------------------------------------------- panel */

.panel {
  position: relative;
}

.panel:focus-visible {
  box-shadow: var(--focus-ring);
  border-radius: var(--radius-lg);
}
</style>
